import { Model } from '@vuex-orm/core';
import BackgroundImageAPI from '@/api/BackgroundImageAPI';

export default class BackgroundImage extends Model {
    public static entity = 'backgroundImage';

    public static fields() {
        return {
            id: this.string(null),
            image: this.string(''),
            title: this.string(''),
            fileSize: this.string(''),
            fileType: this.string(''),
            displayName: this.string(''),
        };
    }

    /**
     * Clears the background images cache
     */
    private static async clearCache() {
        try {
            const cache = await caches.open('background-images-cache');
            await cache.delete('/background-images');
        } catch (e) {
            console.error('Failed to clear cache:', e);
        }
    }

    public static async createNew(formData: FormData) {
        // Clear cache when creating new background image
        await this.clearCache();

        let backgroundImage;

        try {
            backgroundImage = await BackgroundImageAPI.createNew(formData);
        } catch (e) {
            return Promise.reject(e);
        }

        this.insertOrUpdate({
            data: backgroundImage.data,
        });

        return Promise.resolve(backgroundImage);
    }

    public static async getAll() {
        const CACHE_NAME = 'background-images-cache';
        const CACHE_URL = '/background-images';
        const CACHE_MAX_AGE = 24 * 60 * 60 * 1000 * 15; // 15 days in milliseconds

        try {
            // Try to get from cache
            const cache = await caches.open(CACHE_NAME);
            const cachedResponse = await cache.match(CACHE_URL);

            if (cachedResponse) {
                const responseData = await cachedResponse.json();
                const cacheDate = new Date(responseData.timestamp);
                const now = new Date();

                // Check if cache is still valid (less than 15 days old)
                if (now.getTime() - cacheDate.getTime() < CACHE_MAX_AGE) {
                    // Use cached data
                    this.insertOrUpdate({
                        data: responseData.data,
                    });
                    return Promise.resolve({ data: responseData.data });
                }
            }

            // If no valid cache or expired, make the API call
            const backgroundImages = await BackgroundImageAPI.getAll();

            // Store in cache with timestamp
            const cacheData = {
                data: backgroundImages.data,
                timestamp: new Date().toISOString(),
            };

            const cacheResponse = new Response(JSON.stringify(cacheData), {
                headers: { 'Content-Type': 'application/json' },
            });

            await cache.put(CACHE_URL, cacheResponse);

            this.insertOrUpdate({
                data: backgroundImages.data,
            });
            return Promise.resolve(backgroundImages);
        } catch (e) {
            return Promise.reject(e);
        }
    }

    public static async deleteExisting(id: string) {
        // Clear cache when deleting a background image
        await this.clearCache();

        let response;
        try {
            response = await BackgroundImageAPI.delete(id);
        } catch (e) {
            return Promise.reject(e);
        }
        this.delete(id);
        return Promise.resolve(response);
    }

    public id!: string;
    public image!: string;
    public title!: string;
    public fileSize!: string;
    public fileType!: string;
    public displayName!: string;
}
